<script setup lang="ts">
import { defineAsyncComponent } from "vue";

// PrimeVue components
const Avatar = defineAsyncComponent(() => import("primevue/avatar"));
const AvatarGroup = defineAsyncComponent(() => import("primevue/avatargroup"));

// Molecules Components
const WrContactFormPopup = defineAsyncComponent(
  () => import("~/components/molecules/WrContactFormPopup.vue"),
);
const WrAddressCards = defineAsyncComponent(
  () => import("~/components/molecules/WrAddressCards.vue"),
);
const WrAccordion = defineAsyncComponent(
  () => import("~/components/molecules/WrAccordion.vue"),
);
const WrAwardsAndRecognition = defineAsyncComponent(
  () => import("~/components/molecules/WrAwardsAndRecognition.vue"),
);
const WrContactHero = defineAsyncComponent(
  () => import("~/components/molecules/WrContactHero.vue"),
);
const WrDesignStatsCard = defineAsyncComponent(
  () => import("~/components/molecules/WrDesignStatsCard.vue"),
);
const WrProductShowcase = defineAsyncComponent(
  () => import("~/components/molecules/WrProductShowcase.vue"),
);
const WrAiCapabilities = defineAsyncComponent(
  () => import("~/components/molecules/WrAiCapabilities.vue"),
);
const WrBannerCTA = defineAsyncComponent(
  () => import("~/components/molecules/WrBannerCTA.vue"),
);
const WrTechMarquee = defineAsyncComponent(
  () => import("~/components/molecules/WrTechMarquee.vue"),
);
const WrEngagementTimeline = defineAsyncComponent(
  () => import("~/components/molecules/WrEngagementTimeline.vue"),
);

const { isDesktop, isMobile, isTablet } = useDevice();
const colorMode = useColorMode();
const cta = {
  title: "Tell us what you're building",
  subheading:
    "One conversation with our AI Architect is all it takes to get a plan, a timeline and a price.",
  linkText: "Start the Conversation",
};

const items = [
  "All",
  "AI Agents & Products",
  "AI Strategy",
  "Advanced AI",
  "Support & Onboarding",
];
const activeIndex = ref(0);

const setActive = (index: any) => {
  activeIndex.value = index;
};

useHead({
  title:
    "Contact WebReinvent – Award‑Winning Software & Web App Development Agency",
  meta: [
    {
      name: "description",
      content:
        "Get in touch with WebReinvent, top software & web app development agency in India. Let us engineer your next project – worldwide clients, local support.",
    },
    {
      name: "keywords",
      content: "email, phone, contact, creative digital web agency",
    },
    {
      name: "twitter:title",
      content:
        "Contact - Software & Web Application Development Company  - WebReinvent",
    },
    {
      name: "twitter:description",
      content:
        "An award winning creative digital web agency that designs and develops beautiful, fast and responsive websites, application and extensions. Contact us to know more about our tailor made services and dedicated customer support.",
    },
    {
      property: "og:title",
      content:
        "Contact - Software & Web Application Development Company  - WebReinvent",
    },
    {
      property: "og:description",
      content:
        "An award winning creative digital web agency that designs and develops beautiful, fast and responsive websites, application and extensions. Contact us to know more about our tailor made services and dedicated customer support.",
    },
  ],
});
const visible = ref(false);

const openModal = () => {
  visible.value = true;
};

const accordionContent = [
  {
    title: "How long has WebReinvent been in business?",
    description:
      "Founded in 2010 by Pradeep Kumar, WebReinvent brings over 15 years of software engineering expertise and 250+ delivered web, mobile, and AI solutions worldwide. For over a decade and a half, we have maintained an industry-leading defect rate of less than 2.56%. Spearheading the new AI digital era, our flagship products include VaahAgents, LLM KB, and GenicUI.",
    tags: ["All", "AI Strategy"],
  },
  {
    title: "How to choose the right AI development company for your business ?",
    description:
      "Pick a partner that ships, not just promises. WebReinvent is an AI software development company that builds AI you can demo on your own data — like <a href='/products'>VaahAgents</a> and LLM KB — with a verifiable track record and clear post-launch support.",
    tags: ["All", "AI Strategy"],
  },
  {
    title: "Best AI agents for software development teams",
    description:
      "The best agents fit your stack and workflow. WebReinvent's VaahAgents run multi-step tasks end to end, GenicUI turns prompts into production-ready front-end, and LLM KB answers with sources cited — all on one platform you control.",
    tags: ["All", "AI Agents & Products"],
  },
  {
    title: "Which AI agent should your development team use?",
    description:
      "Start where your team loses the most time. WebReinvent's VaahAgents handle repetitive multi-step work, GenicUI covers front-end production, and LLM KB turns your docs into a private knowledge base. Most teams start with one, measure the hours saved, then expand.",
    tags: ["All", "AI Agents & Products", "Advanced AI"],
  },
  {
    title: "AI agents vs traditional development: which is right for you?",
    description:
      "It's not either/or — the strongest teams run both. Agents take the high-volume, well-defined work while your engineers focus on architecture and judgement calls. WebReinvent will help you draw that line in a discovery session.",
    tags: ["All", "AI Strategy", "Advanced AI"],
  },
  {
    title: "The real cost of not using AI agents in development",
    description:
      "It's mostly opportunity cost: slower release cycles, a higher cost per feature, and competitors shipping faster on the same budget. The earlier you deploy something like WebReinvent's VaahAgents, the more ground you recover.",
    tags: ["All", "AI Strategy"],
  },
  {
    title: "What support should you expect from an AI development partner?",
    description:
      "At WebReinvent, that means onboarding and training on your context, a named point of contact, and monitoring and maintenance for anything we deploy. Our agents keep working reliably after go-live, and you get full visibility into every task through our project tooling.",
    tags: ["All", "Support & Onboarding"],
  },
  {
    title: "What can you expect from the AI agent hiring process, and how should you prepare?",
    description:
      "Think of it like onboarding a new team member. You'll start with a discovery session to map the workflows you want to automate, then a pilot where an agent works alongside your team. WebReinvent handles the setup, training, and monitoring, and provides a handover document so your team knows exactly how to run it.",
    tags: ["All", "Support & Onboarding"],
  },
  {
    title: "Why does your development company need an AI agent strategy?",
    description:
      "Adopting agents piecemeal is how teams end up with siloed tools that don't share context. A strategy means knowing which workflows to automate first, how agents share data, and how you'll measure ROI. WebReinvent will audit your setup and build a roadmap you can execute on.",
    tags: ["All", "AI Strategy", "Advanced AI"],
  },
];

const filteredAccordionContent = computed(() => {
  const selectedTag = items[activeIndex.value];
  return selectedTag
    ? accordionContent.filter((item) => item.tags.includes(selectedTag))
    : accordionContent;
});

// JSON-LD structured data
useHead({
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "WebReinvent",
        url: "https://webreinvent.com",
        email: "we@webreinvent.com",
        telephone: "+15852151444",
        sameAs: [
          "https://linkedin.com/company/webreinvent",
          "https://twitter.com/webreinvent",
        ],
        location: [
          {
            "@type": "PostalAddress",
            streetAddress: "7858, 1021 E Lincolnway",
            addressLocality: "Cheyenne",
            addressRegion: "WY",
            postalCode: "82001",
            addressCountry: "US",
          },
          {
            "@type": "PostalAddress",
            streetAddress:
              "Unit 606, 6th Floor, Tower 2 Capital Business Park, Sector 48",
            addressLocality: "Gurugram",
            addressRegion: "Haryana",
            postalCode: "122018",
            addressCountry: "IN",
          },
        ],
      }),
    },
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: accordionContent.map((item) => ({
          "@type": "Question",
          name: item.title,
          acceptedAnswer: {
            "@type": "Answer",
            // Strip HTML markup so the answer is plain text
            text: item.description.replace(/<[^>]*>/g, ""),
          },
        })),
      }),
    },
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://webreinvent.com",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Contact Us",
            item: "https://webreinvent.com/contact-us",
          },
        ],
      }),
    },
  ],
});


// Avatar portrait hover swap: only fetch/decode a member's hover image when
// actually hovered (src swap), instead of every hover image sitting in the DOM
const hoveredAvatar = ref<number | null>(null);

const AvatarImages = [
  {
    image: "/images/avatar8thImg.webp",
    hoverImage: "/images/avatar8thImgHover.webp",
  },
  {
    image: "/images/avatar9thImg.webp",
    hoverImage: "/images/avatar9thImgHover.webp",
  },
  {
    image: "/images/avatar10thImg.webp",
    hoverImage: "/images/avatar10thImgHover.webp",
  },
  {
    image: "/images/avatar12thImg.webp",
    hoverImage: "/images/avatar12thImgHover.webp",
  },
  {
    image: "/images/avatar13thImg.webp",
    hoverImage: "/images/avatar13thImgHover.webp",
  },
  {
    image: "/images/avatar14thImg.webp",
    hoverImage: "/images/avatar14thImgHover.webp",
  },
  {
    image: "/images/avatar15thImg.webp",
    hoverImage: "/images/avatar15thImgHover.webp",
  },
];

// Office video — fullscreen lightbox
const officeLightboxVideoRef = ref<HTMLVideoElement | null>(null);
const isOfficeVideoOpen = ref(false);

// Assembled at runtime so the full URL isn't exposed in the page markup
const officeVideoUrl = [
  'https://pub-e6113e12990e467f911a73ac65295946.r2.dev/',
  'Office-Video-Final.mp4',
].join('');

const openOfficeVideo = async () => {
  isOfficeVideoOpen.value = true;

  await nextTick();

  const video = officeLightboxVideoRef.value;
  if (!video) return;
  video.src = officeVideoUrl;
  video.load();
  video.play().catch((err) => {
    console.error('Video playback failed:', err);
  });
};

const closeOfficeVideo = () => {
  isOfficeVideoOpen.value = false;

  const video = officeLightboxVideoRef.value;
  if (video) {
    video.pause();
    video.currentTime = 0;
    video.removeAttribute('src');
    video.load();
  }
};

const handleOfficeVideoKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && isOfficeVideoOpen.value) {
    closeOfficeVideo();
  }
};

onMounted(() => {
  window.addEventListener('keydown', handleOfficeVideoKeydown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleOfficeVideoKeydown);
});

</script>
<template>
  <div class="max-w-screen-xl mx-auto px-4 relative z-10">
    <!-- 1st Section -->
    <WrContactHero />
    <!-- Tech Marquee Section -->
    <WrTechMarquee :show-heading="false" class="mt-14 lg:mt-24 z-10" />
    <!-- Social Proof Section -->
    <section class="lg:mt-10 mt-10 relative">
      <div class="flex flex-col justify-start items-start">
        <LazyVisibleWrOfficialPartners
          append-class="mt-8 md:mt-16"
          title="Official Partner of"
          :partnersAppendClass="'justify-center items-center'"
          :hydrate-on-visible="{ rootMargin: '100px' }"
        />

             </div>
    </section>
    <!-- Products Section -->
    <WrProductShowcase />
    <div class="flex justify-center lg:mt-12 mt-6">
      <WrButton link="/products">Explore Our Products</WrButton>
    </div>
    <!-- AI Capabilities Section -->
    <WrAiCapabilities />
    <!-- Design Stats Section -->
     <div class="lg:mt-28 mt-10">
      <WrDesignStatsCard />
    </div>
    <!-- Engagement Timeline Section -->
    <section
      class="relative mt-20 z-10 w-screen left-1/2 -translate-x-1/2 py-10 lg:py-24 overflow-hidden grid place-items-center bg-[#EFEFEF] dark:bg-transparent dark:bg-[linear-gradient(119.57deg,_#24156D_10.08%,_#380E53_93.81%)]"
    >
      <NuxtImg
        :src="colorMode.preference === 'dark'
          ? '/images/staff-back-dark.webp'
          : '/images/staff-back.webp'
        "
        width="1735"
        height="889"
        loading="lazy"
        class="max-w-none w-full h-auto absolute top-32 left-0"
        alt="contact-us-background"
        format="webp"
      />
      <div class="max-w-screen-xl mx-auto px-4 lg:px-10 w-full">
        <WrEngagementTimeline />
      </div>
    </section>
    <!-- 2nd Section -->
    <section>
      <div class="lg:mt-[140px] mt-10 relative">
        <span
          class="hidden dark:block size-[1200px] bg-[radial-gradient(62.25%_62.25%_at_53.43%_51.39%,_rgba(42,_1,_73,_0)_0%,_#0B0013_100%)] blur-[150px] rounded-full shadow-[49px_11px_53.1px_15px_#230236] absolute -top-80 opacity-20"
        />
        <div class="flex flex-col justify-center text-center">
          <h2>
            <WrGradientText :text="'Innovation Without Borders'" />
            A Global Network, Built for You
          </h2>
          <p class="text-lg mt-1.5 tracking-[0.5px]">
            Our offices in the USA & India keep us connected to you.
          </p>
        </div>
      </div>
      <div
        class="md:flex md:flex-row flex flex-col 2xl:w-[100%] w-full gap-8 lg:mt-20 mt-4 relative"
      >
        <span
          class="hidden dark:block size-[463px] bg-[#47116B] rounded-full blur-[150px] absolute top-72 -left-72"
        />
        <span
          class="dark:hidden size-[291px] bg-[rgba(143,91,196,0.79)] rounded-full blur-[150px] absolute top-[100%] -left-[5%] opacity-70"
        />
        <div class="border-none relative z-10">
          <NuxtImg
            alt="Web Reinvent Office Image"
            src="/images/WRI_sectionIMage.webp"
            class="h-[478px] md:object-cover rounded-xl cursor-pointer"
            height="518"
            width="566"
            loading="lazy"
            format="webp"
            @click="openOfficeVideo"
          />
          <button
            class="absolute inset-0 z-10 flex items-center justify-center rounded-xl"
            aria-label="Play office video"
            @click="openOfficeVideo"
          >
            <span
              class="flex items-center justify-center size-16 rounded-full bg-white/30 text-[#1a0a2e] shadow-lg transition-all duration-300 hover:scale-110 hover:bg-white/60"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
          </button>
        </div>
        <div class="flex flex-col gap-8 relative z-10">
          <WrAddressCards />
        </div>
      </div>
    </section>

    <!-- 3rd Section  -->
    <section class="lg:mt-28 mt-10 relative">
      <span
        class="hidden dark:block size-[509px] bg-[#22206D99] rounded-full absolute left-[100px] -top-10 blur-[150px]"
      />

      <div class="flex flex-col justify-center text-center">
        <h2>
          <WrGradientText :text="' Consult for Sales Excellence'" />
          Refine Strategies, Divine Growth.
        </h2>
        <p class="text-lg mt-6 tracking-[0.5px]">
          Optimize your sales strategy with expert guidance
        </p>
      </div>
      <div class="lg:mt-12 mt-7 relative">
        <span
          class="hidden dark:block size-[463px] bg-[#47116B] rounded-full blur-[150px] absolute -right-80 top-0"
        />
        <span
          class="dark:hidden size-[515px] bg-[rgba(143,91,196,0.1)] rounded-full blur-[150px] absolute -right-80 top-0"
        />
        <!-- rgba(143,91,196,0.1) -->
        <div
          class="flex max-lg:flex-wrap max-sm:flex-col justify-center items-center gap-[22px] w-full"
        >
          <WrSocialLinks />
        </div>
      </div>
    </section>
    <!-- 4th Section -->
    <section class="lg:mt-[5.5625rem] mt-8">
      <div class="flex flex-col justify-center text-center">
        <h2>
          <WrGradientText :text="'Join the pack'" />
          Shape Digital Innovation!
        </h2>
        <p class="text-lg lg:mt-5 mt-2 tracking-[0.5px]">
          Wonders can happen when different people grow in unison.<br />Wonders
          happen at WebReinvent.
        </p>
      </div>

      <div class="card flex items-center justify-center mt-6">
        <AvatarGroup>
          <template v-for="(avatar, index) in AvatarImages">
            <Avatar
              :pt="{
                image: {
                  alt: 'Avatar Image',
                },
              }"
              :image="
                hoveredAvatar === index && avatar.hoverImage
                  ? avatar.hoverImage
                  : avatar.image
              "
              shape="circle"
              :size="
                isDesktop || isTablet
                  ? 'xlarge'
                  : isMobile
                    ? 'large'
                    : 'normal'
              "
              class="!border-[3px] shadow-[-10px_4px_4px_0px_#00000040] !bg-[var(--color-5)]"
              @mouseenter="hoveredAvatar = index"
              @mouseleave="hoveredAvatar = null"
            />
          </template>
          <Avatar
            :pt="{
              image: {
                alt: 'Avatar Image',
              },
            }"
            label="+100"
            shape="circle"
            :size="
              isDesktop || isTablet ? 'xlarge' : isMobile ? 'large' : 'normal'
            "
            class="!border-[3px] !bg-[var(--bg-avatar-label)] shadow-[-10px_4px_4px_0px_#00000040]"
          />
          <p class="text-lg max-md:hidden tracking-[0.5px] ml-[19px]">
            BREADTH<br />
            & SCALE
          </p>
        </AvatarGroup>
      </div>
      <div class="flex justify-center text-center mt-6">
        <WrButton
          class="w-full"
          target="_blank"
          link="https://webreinvent.ohoapps.com/hrm/v4#/jobs"
        >
          Shape the Future with Us
        </WrButton>
      </div>
    </section>
  </div>
  <!-- 6th Section -->

  <section
    class="relative pb-6 lg:pb-10 overflow-visible grid place-items-center lg:mt-24 mt-12"
  >
    <div
      v-if="colorMode.preference == 'dark'"
      class="absolute w-[2266px] h-[1241px] top-20 rounded-[100%] bg-[linear-gradient(0deg,_#1F013405_31.11%,_#4D27B600_51.33%,_#855BF200_57.98%,_#22206D20_62.05%)] dark:bg-[linear-gradient(0deg,_#1F0134_31.11%,_#4D27B600_51.33%,_#855BF200_57.98%,_#22206D_62.05%)] blur-[100px] dark:blur-[100px]"
    >
      <div
        class="w-[2260px] h-[1440px] bg-white dark:bg-[var(--color-5)] rounded-[100%] absolute top-[200px] left-0"
      ></div>
    </div>
    <NuxtImg
      v-if="colorMode.preference != 'dark'"
      src="/images/cms-back-laravel.webp"
      class="absolute -top-96 left-1/2 -translate-x-1/2 min-w-[3745px] min-h-[2349]"
      width="3745"
      height="2349"
      alt="cms-back"
      loading="lazy"
      format="webp"
    />

    <div class="rising-stars top-0">
      <div style="height: 4px; width: 4px"></div>
      <div style="height: 2px; width: 2px"></div>
      <div style="height: 1px; width: 1px"></div>
    </div>

    <div
      class="max-w-screen-xl mx-auto px-4 w-full grid md:grid-cols-2 gap-6 relative z-10"
    >
      <div class="max-md:text-center mx-auto max-w-[504px]">
        <h4>
          <WrGradientText
            text="Get all your questions"
            append-class="py-0 md:py-4"
          />
          <span>answered here</span>
        </h4>
        <p class="mt-5 pr-4 text-lg tracking-[0.5px]">
          Everything you need to know about working with us.
        </p>

        <ul
          class="mt-8 list-disc max-md:gap-y-4 max-md:max-w-max max-md:mx-auto max-md:grid max-md:grid-cols-2 max-sm:grid-cols-1 max-sm:mx-auto text-[var(--secondary-foreground)] md:space-y-4 list-inside text-left"
        >
          <li
            v-for="(item, index) in items"
            :key="index"
            @click="setActive(index)"
            :class="{
              'cursor-pointer': true,
              active: activeIndex === index,
            }"
            class="tracking-[0.5px]"
          >
            {{ item }}
          </li>
        </ul>
        <WrButton container-class="mt-8 max-md:mx-auto" @click="openModal"
          >Connect with us</WrButton
        >
      </div>
      <div class="grow">
        <WrAccordion :Content="filteredAccordionContent" />
      </div>
    </div>
  </section>

  <!-- Final CTA Section -->
  <WrBannerCTA
    :heading="cta.title"
    :paragraph-text="cta.subheading"
    :button-text="cta.linkText"
  />
 <div
          class="w-full flex flex-col justify-start lg:mt-[3.875rem] mt-[2rem] px-4"
        >
          <h5 class="text-center">
            <WrGradientText text="Trusted for excellence and efficiency" />
          </h5>
          <p
            class="text-lg text-[var(--foreground)] mb-10 text-center tracking-[0.5px]"
          >
            WebReinvent is trusted by leading organisations to enhance their
            development and operational efficiency.
          </p>
          <div class="w-full max-w-screen-xl mx-auto">
            <LazyVisibleWrTestimonialCardCarousel :hydrate-on-visible="{ rootMargin: '100px' }" />
          </div>
        </div>


  <!-- 7th Section -->
  <WrAwardsAndRecognition />
  <WrContactFormPopup :isOpen="visible" @close="visible = false" class="z-30" />

  <!-- Office Video Lightbox -->
  <Teleport to="body">
    <div
      v-show="isOfficeVideoOpen"
      class="fixed inset-0 z-[1000000] flex items-center justify-center"
      @click.self="closeOfficeVideo"
    >
      <!-- Dark Backdrop -->
      <div
        class="absolute inset-0 bg-black/90"
        @click="closeOfficeVideo"
      />

      <!-- Video Container -->
      <div class="relative w-[90vw] max-w-[1200px] aspect-video">
        <button
          class="absolute top-4 right-4 z-20 flex items-center justify-center bg-transparent text-white transition-opacity hover:opacity-70"
          aria-label="Close video"
          @click="closeOfficeVideo"
        >
          <WrSVGIcon name="WrCrossIcon" class="size-5" />
        </button>

        <video
          ref="officeLightboxVideoRef"
          controls
          playsinline
          preload="none"
          class="h-full w-full object-contain bg-black"
        />
      </div>
    </div>
  </Teleport>
</template>

<style scoped></style>
